import { Link } from "react-router-dom";
import { ScrollReveal } from "@/components/base/ScrollAnimations";

const siteSections = [
  {
    title: "Main Pages",
    links: [
      { label: "Home", href: "/" },
      { label: "About", href: "/about" },
      { label: "Programs", href: "/programs" },
      { label: "News", href: "/news" },
      { label: "Collaborate", href: "/collaborate" },
      { label: "Contact", href: "/contact" },
    ],
  },
  {
    title: "Innovation Wing",
    links: [
      { label: "Innovation Wing Overview", href: "/wings/innovation" },
      { label: "PIIC", href: "/piic" },
      { label: "PTIM", href: "/ptim" },
      { label: "Training (FSD / PSD / Black Belt)", href: "/training" },
      { label: "TIE Experts' Platform", href: "/experts" },
    ],
  },
  {
    title: "Research Wing",
    links: [
      { label: "Research Wing Overview", href: "/wings/research" },
      { label: "PIARC", href: "/piarc" },
      { label: "IJTAI Journal", href: "/ijtai" },
      { label: "AIRCon Conference", href: "/aircon" },
    ],
  },
  {
    title: "Readiness & Recognition",
    links: [
      { label: "Readiness & Recognition Overview", href: "/wings/readiness" },
      { label: "Innovation Readiness Certification", href: "/wings/readiness" },
      { label: "Textile Innovation Awards", href: "/wings/readiness" },
    ],
  },
  {
    title: "Expo Wing",
    links: [
      { label: "Expo Wing Overview", href: "/wings/expo" },
      { label: "Textile Innovation Expo (External)", href: "https://www.textileinnovationexpo.com/" },
      { label: "Concurrent Expos", href: "/wings/expo" },
    ],
  },
  {
    title: "Innovation Cells",
    links: [
      { label: "Textile Engineering Cell", href: "/cells/textile-engineering" },
      { label: "Management Excellence Cell", href: "/cells/management-excellence" },
      { label: "Textile Processing Cell", href: "/cells/textile-processing" },
      { label: "Textile Materials Cell", href: "/cells/textile-materials" },
      { label: "Marketing & Merchandising Cell", href: "/cells/marketing-merchandising" },
      { label: "Textile Efficiency Cell", href: "/cells/textile-efficiency" },
    ],
  },
  {
    title: "Ecosystem & Partners",
    links: [
      { label: "Ecosystem", href: "/ecosystem" },
      { label: "Technology Partners", href: "/technology-partners" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", href: "/privacy-policy" },
      { label: "Terms & Conditions", href: "/terms-conditions" },
    ],
  },
];

function isExternal(href: string) {
  return href.startsWith("http://") || href.startsWith("https://");
}

export default function SitemapPage() {
  return (
    <main className="min-h-screen">
      {/* Hero */}
      <section className="relative min-h-[280px] md:min-h-[360px] flex items-center justify-center overflow-hidden bg-tie-navy">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract minimalist geometric network pattern in deep navy and warm gold tones, connected nodes and lines representing a digital sitemap structure, dark corporate background, clean modern design, no blue no purple, horizontal composition"
            alt=""
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-tie-navy/80 via-tie-navy/60 to-tie-navy/90" />
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 pt-24 pb-10 md:pb-14 text-center">
          <ScrollReveal>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 mb-5 md:mb-6">
              <span className="text-xs md:text-sm text-white/80 font-medium tracking-wide uppercase">
                / Site Map
              </span>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              Sitemap
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={200}>
            <p className="mt-4 md:mt-5 text-base md:text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">
              A complete guide to every page on the Textile Innovation Exchange website.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Sitemap Grid */}
      <section className="bg-white py-16 md:py-24">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
              {siteSections.map((section, sectionIndex) => (
                <ScrollReveal key={section.title} delay={sectionIndex * 100}>
                  <div className="bg-tie-grey-light rounded-2xl p-5 md:p-6 border border-tie-grey-dark">
                    <h2 className="text-base md:text-lg font-bold text-tie-text-primary mb-4 pb-3 border-b border-tie-grey-dark">
                      {section.title}
                    </h2>
                    <ul className="space-y-2.5">
                      {section.links.map((link) => {
                        const external = isExternal(link.href);
                        return external ? (
                          <li key={link.href + link.label}>
                            <a
                              href={link.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-sm text-tie-text-secondary hover:text-tie-teal transition-colors"
                            >
                              {link.label}
                              <i className="ri-external-link-line ml-1.5 text-xs" />
                            </a>
                          </li>
                        ) : (
                          <li key={link.href + link.label}>
                            <Link
                              to={link.href}
                              className="inline-flex items-center text-sm text-tie-text-secondary hover:text-tie-teal transition-colors"
                            >
                              {link.label}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}